import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
  AccordionItemState
} from 'react-accessible-accordion';

import Search from '../Search';
import { Diary, Help, Heart, Gear, Logout, Plus } from '../icons';
import profileIcons from '../icons/profileIcons';

import './Header.scss';

const MobileMenu = ({
  isAuthenticated = false,
  menuItems = [],
  profile = null,
  menuClose,
  openModal,
  onChangeProfileClick,
  handleLogout
}) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <nav className="mobile-menu">
      {isAuthenticated && (
        <div className="header__secondary-item header__search-container">
          <Search menuClose={menuClose} />
        </div>
      )}

      <div className="header__secondary-nav--burger">
        {isAuthenticated && (
          <>
            <NavLink
              to="/diary"
              className="header__secondary-item--link-burger header__secondary-item--link-burger-with-icon"
              onClick={menuClose}
            >
              <Diary width="34" height="34" viewBox="0 0 32 32" />
              <span className="header__secondary-item--link-burger-title">
                {t('nav.diary')}
              </span>
            </NavLink>

            <Accordion allowZeroExpanded className="header__secondary-accordion">
              {/* Ask List Accordion */}
              <AccordionItem>
                <AccordionItemHeading className="header__secondary-accordion-item-heading header__secondary-accordion-item-heading-with-icon">
                  <AccordionItemButton className="header__secondary-accordion-item-button">
                    <AccordionItemState>
                      {({ expanded }) => (
                        <div className="header__secondary-accordion-item-button-wrapper">
                          <Help
                            width="40"
                            height="40"
                            viewBox="0 0 32 32"
                            className="header-button__account-greeting-icon"
                          />
                          <span>Ask List</span>
                        </div>
                      )}
                    </AccordionItemState>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className="header__secondary-accordion-panel">
                  {/* Action Button: Add Question */}
                  <button
                    type="button"
                    className="header__secondary-item--link-burger btn-link"
                    onClick={(e) => {
                      menuClose();
                      openModal(e);
                    }}
                  >
                    <Plus className="header__secondary-item--link-burger-icon" width="16" height="16" fill="white" />
                    {t('askList.addAQuestion')}
                  </button>
                  <NavLink onClick={menuClose} className="header__secondary-item--link-burger" to="/questions">
                    {t('askList.seeAllQuestions')}
                  </NavLink>
                </AccordionItemPanel>
              </AccordionItem>

              {/* Account Accordion */}
              <AccordionItem>
                <AccordionItemHeading className="header__secondary-accordion-item-heading header__secondary-accordion-item-heading-with-icon">
                  <AccordionItemButton className="header__secondary-accordion-item-button">
                    <div className="header__secondary-accordion-item-button-wrapper">
                      {profile?.icon && profileIcons[profile.icon] ? (
                        <img
                          src={profileIcons[profile.icon]}
                          width="40"
                          height="40"
                          className="header-button__account-greeting-icon"
                          alt=""
                        />
                      ) : (
                        <Heart width="40" height="40" className="header-button__account-greeting-icon" />
                      )}
                      <span>Account</span>
                    </div>
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel className="header__secondary-accordion-panel">
                  {/* Action Button: Change Profile */}
                  <button
                    type="button"
                    className="header__secondary-item--link-burger btn-link"
                    onClick={() => {
                      onChangeProfileClick();
                      menuClose();
                    }}
                  >
                    <div>
                      {profile?.firstName && (
                        <div className="header__secondary-item--profile-name">{profile.firstName}</div>
                      )}
                      {t('nav.changeProfile')}
                    </div>
                  </button>
                  <NavLink onClick={menuClose} className="header__secondary-item--link-burger" to="/options">
                    <Gear className="header__secondary-item--link-burger-icon" width="24" height="24" fill="white" />
                    {t('nav.options')}
                  </NavLink>
                </AccordionItemPanel>
              </AccordionItem>
            </Accordion>
          </>
        )}

        {/* Dynamic Menu Items */}
        {menuItems?.map(({ url, title, ID }) => (
          <NavLink
            key={ID}
            to={url}
            onClick={menuClose}
            className="header__secondary-item--link-burger header__secondary-item--link-burger-no-icon"
          >
            {title}
          </NavLink>
        ))}

        {isAuthenticated && (
          <>
            <NavLink onClick={menuClose} className="header__secondary-item--link-burger header__secondary-item--link-burger-no-icon" to="/glossary">
              {t('nav.glossary')}
            </NavLink>
            <NavLink onClick={menuClose} className="header__secondary-item--link-burger header__secondary-item--link-burger-no-icon" to="/videos">
              Videos
            </NavLink>
            <button
              type="button"
              className="header__secondary-item--link-burger header__secondary-item--link-burger-with-icon btn-link"
              onClick={() => {
                menuClose();
                handleLogout();
              }}
            >
              <Logout className="header-button__account-icon header-button__account-icon--logout" width="32" height="32" viewBox="0 0 32 32" />
              <span className="header__secondary-item--link-burger-title">{t('nav.logout')}</span>
            </button>
          </>
        )}
      </div>

      <div className="header__secondary-nav--burger-legal">
        <div className="header__secondary-nav--burger-legal-links">
          <NavLink exact="true" className="header__secondary-nav--burger-legal-link" to="/subpage/terms-of-service" onClick={menuClose}>Terms of Service</NavLink>
          <NavLink exact="true" className="header__secondary-nav--burger-legal-link" to="/subpage/privacy-and-confidentiality" onClick={menuClose}>{t('nav.privacy')}</NavLink>
        </div>
        <p className="header__second-nav--burger-legal-copy">
          {/* Dynamic copyright year */}
          &copy; {currentYear} {t('nav.copyright', 'Brand Name')}
        </p>
      </div>
    </nav>
  );
};

MobileMenu.propTypes = {
  isAuthenticated: PropTypes.bool,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  profile: PropTypes.shape({
    firstName: PropTypes.string,
    icon: PropTypes.string
  }),
  menuClose: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  onChangeProfileClick: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired
};

export default MobileMenu;
